/**
 * SVG Editor Service
 * Parse and modify SVG elements
 */

export interface SvgElement {
  id: string;
  tagName: string;
  attributes: Record<string, string>;
  path: number[]; // Index path to find element in DOM
}

export interface SvgDocument {
  width: number;
  height: number;
  viewBox: string;
  elements: SvgElement[];
  originalSvg: string;
}

/**
 * Parse SVG string into editable structure
 */
export function parseSvg(svgString: string): SvgDocument | null {
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(svgString, 'image/svg+xml');
    const svg = doc.querySelector('svg');
    
    if (!svg) return null;
    
    const width = parseFloat(svg.getAttribute('width') || '100');
    const height = parseFloat(svg.getAttribute('height') || '100');
    const viewBox = svg.getAttribute('viewBox') || `0 0 ${width} ${height}`;
    
    const elements: SvgElement[] = [];
    let elementId = 0;
    
    // Extract editable elements
    const editableTags = ['path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'text', 'g'];
    
    function extractElements(parent: Element, path: number[] = []) {
      Array.from(parent.children).forEach((child, index) => {
        const currentPath = [...path, index];
        
        if (editableTags.includes(child.tagName.toLowerCase())) {
          const attributes: Record<string, string> = {};
          
          Array.from(child.attributes).forEach(attr => {
            attributes[attr.name] = attr.value;
          });
          
          elements.push({
            id: `element-${elementId++}`,
            tagName: child.tagName.toLowerCase(),
            attributes,
            path: currentPath,
          });
        }
        
        // Recurse into groups
        if (child.children.length > 0) {
          extractElements(child, currentPath);
        }
      });
    }
    
    extractElements(svg);
    
    return {
      width,
      height,
      viewBox,
      elements,
      originalSvg: svgString,
    };
  } catch {
    return null;
  }
}

/**
 * Apply changes to SVG and return new string
 */
export function applySvgChanges(
  originalSvg: string,
  elementPath: number[],
  changes: Record<string, string>
): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(originalSvg, 'image/svg+xml');
  const svg = doc.querySelector('svg');
  
  if (!svg) return originalSvg;
  
  // Navigate to element using path
  let element: Element | null = svg;
  for (const index of elementPath) {
    if (element && element.children[index]) {
      element = element.children[index];
    } else {
      return originalSvg;
    }
  }
  
  if (!element) return originalSvg;
  
  // Apply changes
  Object.entries(changes).forEach(([key, value]) => {
    if (value === '') {
      element!.removeAttribute(key);
    } else {
      element!.setAttribute(key, value);
    }
  });
  
  // Serialize back to string
  const serializer = new XMLSerializer();
  return serializer.serializeToString(svg);
}

/**
 * Get common editable properties for an element type
 */
export function getEditableProperties(tagName: string): string[] {
  const common = ['fill', 'stroke', 'stroke-width', 'opacity', 'transform'];
  
  switch (tagName) {
    case 'rect':
      return [...common, 'x', 'y', 'width', 'height', 'rx', 'ry'];
    case 'circle':
      return [...common, 'cx', 'cy', 'r'];
    case 'ellipse':
      return [...common, 'cx', 'cy', 'rx', 'ry'];
    case 'line':
      return [...common, 'x1', 'y1', 'x2', 'y2'];
    case 'path':
      return [...common, 'd'];
    case 'text':
      return [...common, 'x', 'y', 'font-size', 'font-family', 'text-anchor'];
    case 'polygon':
    case 'polyline':
      return [...common, 'points'];
    case 'g':
      return ['transform', 'opacity'];
    default:
      return common;
  }
}

/**
 * Check if a value looks like a color
 */
export function isColorValue(value: string): boolean {
  if (!value) return false;
  return (
    value.startsWith('#') ||
    value.startsWith('rgb') ||
    value.startsWith('hsl') ||
    ['none', 'transparent', 'currentColor'].includes(value) ||
    /^[a-z]+$/i.test(value) // named colors
  );
}

/**
 * Convert color to hex for color picker
 */
export function colorToHex(color: string): string {
  if (!color || color === 'none' || color === 'transparent') {
    return '#000000';
  }
  
  if (color.startsWith('#')) {
    if (color.length === 4) {
      // Expand shorthand #RGB to #RRGGBB
      return `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`;
    }
    return color;
  }
  
  // Handle rgb()
  const rgbMatch = color.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1]).toString(16).padStart(2, '0');
    const g = parseInt(rgbMatch[2]).toString(16).padStart(2, '0');
    const b = parseInt(rgbMatch[3]).toString(16).padStart(2, '0');
    return `#${r}${g}${b}`;
  }
  
  // Named colors - return as-is, browser will handle
  return '#000000';
}

/**
 * Format SVG string with proper indentation
 */
export function formatSvg(svgString: string): string {
  let formatted = '';
  let indent = 0;
  const lines = svgString
    .replace(/>\s*</g, '>\n<')
    .split('\n');
  
  lines.forEach(line => {
    const trimmed = line.trim();
    if (!trimmed) return;
    
    // Decrease indent for closing tags
    if (trimmed.startsWith('</')) {
      indent = Math.max(0, indent - 1);
    }
    
    formatted += '  '.repeat(indent) + trimmed + '\n';
    
    // Increase indent for opening tags (not self-closing)
    if (trimmed.startsWith('<') && !trimmed.startsWith('</') && !trimmed.endsWith('/>')) {
      indent++;
    }
  });
  
  return formatted.trim();
}
